// 2D layered robot: absolutely-positioned transparent PNG parts stacked by z-index.
// Every part art shares the same portrait frame (ratio 848:1264), so stacking at inset:0 aligns them.
const RATIO = 848 / 1264; // width / height
const ORDER = ["legs", "arms", "torso", "head"]; // back -> front
const AMBER_HUE = 42; // native accent hue of the part art

// CPU personality -> signature idle animation class
const IDLE_BY_CPU = {
  cpu_logic: "idle-logic",
  cpu_hyper: "idle-hyper",
  cpu_rust: "idle-rust",
  cpu_holo: "idle-holo",
};

function hexHue(hex) {
  if (!hex || hex[0] !== "#" || hex.length < 7) return AMBER_HUE;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d === 0) return AMBER_HUE;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  return h < 0 ? h + 360 : h;
}

export default function LayeredStack({ layers = {}, core = "#FFB300", size = 200, state = "idle", rig = "bipedal", cpu = "cpu_logic" }) {
  const h = size;
  const w = Math.round(size * RATIO);
  const dim = state === "off";
  const idleClass = dim ? "" : rig === "hover" ? "idle-hover" : (IDLE_BY_CPU[cpu] || "idle-logic");
  // recolor the amber accent lines toward the chosen core colour
  const rot = Math.round(hexHue(core) - AMBER_HUE);
  const tint = dim ? "grayscale(0.7)" : `hue-rotate(${rot}deg) saturate(1.25)`;
  return (
    <div
      data-testid="robot-layered-stack"
      className={idleClass}
      style={{
        width: w,
        height: h,
        position: "relative",
        opacity: dim ? 0.5 : 1,
        filter: dim ? "grayscale(0.7)" : `drop-shadow(0 6px 16px ${core}66)`,
      }}
    >
      {/* core-color aura */}
      <div
        aria-hidden
        className={dim ? "" : "pulse-glow"}
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 46%, ${core}3d, transparent 60%)`,
          zIndex: 0,
        }}
      />
      {ORDER.map((k, i) =>
        layers && layers[k] ? (
          <img
            key={k}
            src={layers[k]}
            alt={k}
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              zIndex: i + 1,
              pointerEvents: "none",
              filter: tint,
            }}
          />
        ) : null
      )}
    </div>
  );
}
